// Shape of API responses, mirrored from backend record types.

export type ProspectStatus =
  | "NEW"
  | "CONTACTED"
  | "ENGAGED"
  | "QUALIFIED"
  | "NEGOTIATING"
  | "CLOSING"
  | "WON"
  | "LOST"
  | "UNRESPONSIVE"
  | "RE_ENGAGEMENT"
  | "ARCHIVED";

export type EmailDirection = "OUTBOUND" | "INBOUND";

export type EmailStatus =
  | "DRAFT"
  | "QUEUED"
  | "SENT"
  | "DELIVERED"
  | "BOUNCED"
  | "COMPLAINED"
  | "OPENED"
  | "REPLIED"
  | "FAILED"
  | "UNSUBSCRIBED"
  | "REJECTED";

/** Whether the agent is working a prospect. Mirrors the backend enum. */
export type FlowState = "NOT_STARTED" | "RUNNING" | "PAUSED" | "STOPPED";

/** Who releases an outbound message: a human, or the agent itself. */
export type ApprovalMode = "REVIEW" | "AUTO";
/**
 * SA 1.14 — what a strategy does when an address that unsubscribed is loaded
 * again: drop it (SKIP) or import it afresh with the opt-out lifted (ALLOW).
 */
export type OptOutReimportMode = "SKIP" | "ALLOW";

export interface AuthenticatedUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
}

export interface ProspectSummary {
  id: string;
  email: string;
  fullName: string | null;
  status: ProspectStatus;
  currentPhaseSlug: string | null;
  flowState: FlowState;
  nextActionAt: string | null;
  lastActivityAt: string | null;
  createdAt: string;
}

export interface ProspectDetail {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  status: ProspectStatus;
  currentPhaseSlug: string | null;
  currentPhaseName: string | null;
  flowState: FlowState;
  /** The prospect's own override; null means it inherits from the strategy. */
  approvalMode: ApprovalMode | null;
  /** What will actually happen to the next message, precedence already applied. */
  effectiveApprovalMode: ApprovalMode;
  /** Message awaiting a human, if this conversation is blocked on one. */
  pendingDraftId: string | null;
  tags: string[];
  timezone: string | null;
  /** Strategy-declared extra fields (prospect_schema); {} when none are set. */
  customFields: Record<string, unknown>;
  consentGivenAt: string | null;
  unsubscribedAt: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  nextActionAt: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  updatedAt: string;
  strategyId: string;
  strategySlug: string;
}

export interface PageResult<T> {
  items: T[];
  page: number;
  size: number;
  total: number;
}

export interface ThreadSummary {
  id: string;
  subject: string;
  lastMessageAt: string | null;
  createdAt: string;
  messageCount: number;
}

/** Where an email lands as a phase example pattern, and what that phase already carries. */
export interface PhaseExampleSummary {
  strategySlug: string;
  phaseSlug: string;
  exampleCount: number;
  /** Rough prompt cost of all the phase's examples together, per email written. */
  approxTokens: number;
  alreadySaved: boolean;
}

export interface MessageView {
  id: string;
  direction: EmailDirection;
  status: EmailStatus;
  subject: string;
  bodyText: string;
  bodyHtml: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  bounceReason: string | null;
  createdAt: string;
  /** The ingest-time zero-trust screen flagged this inbound as a possible prompt injection. */
  injectionSuspected: boolean;
}

export interface TransitionView {
  id: string;
  fromPhaseId: string | null;
  toPhaseId: string;
  /** Resolved server-side; null for the initial transition (no source phase). */
  fromPhaseName: string | null;
  /** Null only if the phase no longer exists in the strategy document. */
  toPhaseName: string | null;
  trigger: string;
  notes: string | null;
  createdAt: string;
}

export interface StrategySummary {
  id: string;
  slug: string;
  name: string;
  version: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  phaseCount: number;
  ragTopK: number;
  similarityThreshold: number;
}

export interface PhaseView {
  id: string;
  slug: string;
  name: string;
  orderIndex: number;
  terminal: boolean;
  goal: string | null;
}

export interface StrategyDetail {
  strategy: StrategySummary;
  phases: PhaseView[];
}

/**
 * Operator-injected guidance for one phase of one strategy. Active entries are
 * spliced into the email-generation system prompt ahead of retrieved knowledge
 * and training notes.
 */
export interface ManualPromptEntry {
  id: string;
  phaseId: string;
  content: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type FeedbackType = "MISTAKE" | "GOOD_BEHAVIOR" | "BAD_BEHAVIOR" | "PATTERN";

export interface FeedbackEntry {
  id: string;
  strategyId: string;
  phaseId: string | null;
  relatedEmailId: string | null;
  type: FeedbackType;
  scenario: string;
  exampleEmail: string | null;
  correction: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Feedback distillation — the analyst's verdicts on captured feedback, and
 * where each one would be written if approved.
 */
export type ProposalKind = "NEW_RULE" | "MERGE" | "KEEP_AS_EXAMPLE" | "NO_ACTION";

export type ProposalTarget = "AI_RULE" | "MANUAL_PROMPT" | "FEEDBACK_RULE" | "NONE";

export type ProposalStatus = "PENDING" | "APPLIED" | "REJECTED";

export type ReviewStatus = "PENDING" | "RUNNING" | "READY" | "FAILED";

export type ReviewTrigger = "CAPTURE" | "MANUAL";

export interface FeedbackProposal {
  id: string;
  reviewId: string;
  kind: ProposalKind;
  target: ProposalTarget;
  targetPhaseId: string | null;
  targetFeedbackId: string | null;
  targetRuleIndex: number | null;
  proposedText: string;
  rationale: string;
  sourceFeedbackIds: string[];
  status: ProposalStatus;
  /** What actually landed — differs from proposedText when the operator edited it. */
  appliedText: string | null;
  /** Where it landed, e.g. "phase:discovery:ai_rules[2]". */
  appliedRef: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export interface FeedbackReview {
  id: string;
  trigger: ReviewTrigger;
  sourceFeedbackId: string | null;
  status: ReviewStatus;
  summary: string | null;
  error: string | null;
  entriesConsidered: number;
  createdAt: string;
  updatedAt: string;
  proposals: FeedbackProposal[];
}

export type IngestJobStatus =
  | "PENDING"
  | "EXTRACTING"
  | "CHUNKING"
  | "EMBEDDING"
  | "SUMMARIZING"
  | "SUCCEEDED"
  | "FAILED";

export interface IngestJobView {
  id: string;
  strategyId: string;
  contentId: string | null;
  sourceLabel: string;
  sourceKind: string;
  sourceMime: string | null;
  sourceSizeBytes: number | null;
  topics: string[];
  status: IngestJobStatus;
  chunkCount: number;
  tokenCount: number;
  embeddingModel: string | null;
  lastError: string | null;
  attemptCount: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

/**
 * Lifecycle of the most recent digest generation. Generation is a queued job —
 * distilling a full corpus takes minutes — so the client polls the digest while
 * the status is in-flight instead of awaiting the POST.
 */
export type KnowledgeDigestStatus =
  | "PENDING"
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED";

/** GET/PUT /api/v1/strategies/{id}/knowledge-digest */
export interface KnowledgeDigestView {
  digest: string | null;
  model: string | null;
  updatedAt: string | null;
  /** Requested digest length in words, and the server-enforced bounds. */
  targetWords: number;
  minTargetWords: number;
  maxTargetWords: number;
  /** null when no generation has ever been requested for this strategy. */
  status: KnowledgeDigestStatus | null;
  /** Operator-facing reason the most recent generation failed. */
  error: string | null;
}

/** True while a generation is outstanding, i.e. keep polling. */
export function isDigestInFlight(
  status: KnowledgeDigestStatus | null | undefined,
): boolean {
  return status === "PENDING" || status === "RUNNING";
}

/** GET/PUT /api/v1/strategies/{id}/knowledge-settings */
export interface KnowledgeSettingsView {
  /** Words requested for the per-file summary written during ingest. */
  summaryTargetWords: number;
  minSummaryTargetWords: number;
  maxSummaryTargetWords: number;
}

/** GET /api/v1/strategies/{id}/ingest/supported-types */
export interface IngestSupportedTypes {
  extensions: string[];
  maxFileSizeBytes: number;
}

export interface CourseContentView {
  id: string;
  strategyId: string;
  sourceLabel: string;
  sourceKind: string;
  chunkCount: number;
  ingestedAt: string;
  // 1.16 — null when summarisation was skipped or failed; never blocks ingest.
  summary: string | null;
  summaryModel: string | null;
}

export type DocumentDraftStatus =
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "SENT";

export interface DocumentTemplateView {
  id: string;
  strategyId: string;
  slug: string;
  name: string;
  description: string | null;
  bodyTemplate: string;
  placeholders: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentDraftSummary {
  id: string;
  strategyId: string;
  prospectId: string;
  prospectEmail: string | null;
  prospectFullName: string | null;
  templateId: string;
  templateSlug: string | null;
  templateName: string | null;
  status: DocumentDraftStatus;
  bodyLength: number;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentDraftDetail {
  id: string;
  strategyId: string;
  prospectId: string;
  prospectEmail: string | null;
  prospectFullName: string | null;
  templateId: string;
  templateSlug: string | null;
  templateName: string | null;
  status: DocumentDraftStatus;
  bodyText: string;
  draftContext: unknown;
  rejectionReason: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  sentEmailId: string | null;
  createdAt: string;
  updatedAt: string;
}

// EPIC-005 — analytics endpoint shapes (mirror AnalyticsController records).

export interface PhaseStatusBreakdown {
  phaseSlug: string;
  phaseName: string;
  orderIndex: number;
  prospectCount: number;
  byStatus: Record<string, number>;
}
export interface PipelineFunnel {
  strategyId: string;
  strategySlug: string;
  phases: PhaseStatusBreakdown[];
  unassignedCount: number;
}
export interface ReplyRateTimePoint {
  date: string;       // ISO LocalDate
  sent: number;
  replied: number;
  replyRatePct: string;  // BigDecimal serialized as string
}
export interface ReplyRateSeries {
  strategyId: string;
  from: string;
  to: string;
  totalSent: number;
  totalReplied: number;
  overallRatePct: string;
  points: ReplyRateTimePoint[];
}
export interface ConversionPair {
  fromPhaseSlug: string | null;
  toPhaseSlug: string;
  count: number;
}
export interface PhaseConversion {
  strategyId: string;
  from: string;
  to: string;
  pairs: ConversionPair[];
}
export interface CostPoint {
  date: string;
  purpose: string;
  costEur: string;
  requestCount: number;
}
export interface LlmCostSeries {
  strategyId: string;
  from: string;
  to: string;
  totalCostEur: string;
  totalByPurpose: Record<string, string>;
  /** Keyed by LlmCostSource: REPORTED (provider-billed) | ESTIMATED | UNPRICED. */
  totalByCostSource: Record<string, string>;
  points: CostPoint[];
}
export interface Deliverability {
  strategyId: string;
  from: string;
  to: string;
  sent: number;          // attempted in backend
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  deliveredRatePct: string;
  bouncePer1k: string;
  complaintPer1k: string;
}

// EPIC-005 — settings endpoint shapes (SettingsController).

export interface SendWindowView {
  startHour: number | null;
  endHour: number | null;
  timezone: string | null;
  days: string[] | null;
}
export interface RuntimeView {
  slug: string;
  defaultSenderName: string | null;
  /**
   * SA 1.6 — the salutation opening every agent-composed email and the sign-off
   * closing it, interpolated against the same namespace as an email template.
   * Null means the model still writes both ends itself.
   */
  greeting: string | null;
  /** Used when the greeting's variables cannot be resolved (a prospect with no name). */
  greetingFallback: string | null;
  signature: string | null;
  sendWindow: SendWindowView | null;
  maxEmailsPerDay: number | null;
  /** SA 1.13 — default ceiling per recipient domain per day. Null = unlimited. */
  maxEmailsPerDomainPerDay: number | null;
  /** SA 1.13 — per-domain overrides of the default, keyed by bare lowercase domain. */
  domainDailyLimits: Record<string, number>;
  minDelayBetweenEmailsHours: number | null;
  /** Always resolved — never null — so the control shows what actually applies. */
  approvalMode: ApprovalMode;
  /** New conversations opened per day. Null or 0 = only operators start prospects. */
  maxNewProspectsPerDay: number | null;
  startPacingTimezone: string | null;
  /** SA 1.14 — always resolved: the default is SKIP. */
  optOutReimport: OptOutReimportMode;
  /** Whether meeting such an address raises a warning on the Errors page. Default true. */
  optOutReimportAlert: boolean;
  active: boolean;
  version: string;
}
export interface RuntimeUpdate {
  defaultSenderName: string;
  /** Blank clears the stored value, turning that end back over to the model. */
  greeting: string;
  greetingFallback: string;
  signature: string;
  sendWindow: SendWindowView | null;
  maxEmailsPerDay: number | null;
  /** Null leaves the stored value alone; 0 or less clears the limit. */
  maxEmailsPerDomainPerDay: number | null;
  /** Null leaves the stored map alone; an empty object clears every override. */
  domainDailyLimits: Record<string, number> | null;
  minDelayBetweenEmailsHours: number | null;
  approvalMode: ApprovalMode | null;
  maxNewProspectsPerDay: number | null;
  startPacingTimezone: string | null;
  /** Null leaves the stored value alone. */
  optOutReimport: OptOutReimportMode | null;
  optOutReimportAlert: boolean | null;
}
export interface CredentialKeyView {
  key: string;
  updatedAt: string | null;
}

// SA 2.9 — AI model settings. A null field means "inherit the deployment
// default"; `effective` reports what each purpose actually resolves to, so a
// blank field is never a guess.
export interface ModelOption {
  id: string;
  label: string;
  recommended: boolean;
  promptUsdPer1k: string;
  completionUsdPer1k: string;
  supportsSampling: boolean;
}
export interface AiView {
  model: string | null;
  fallbackModels: string[] | null;
  emailModel: string | null;
  analysisModel: string | null;
  extractionModel: string | null;
  documentModel: string | null;
  summaryModel: string | null;
  effective: Record<string, string>;
}
export interface AiUpdate {
  model: string | null;
  fallbackModels: string[] | null;
  emailModel: string | null;
  analysisModel: string | null;
  extractionModel: string | null;
  documentModel: string | null;
  summaryModel: string | null;
}

// EPIC-005 — strategy editor + dynamic prospect form.

export interface ProspectFieldView {
  key: string;
  label: string;
  type: "string" | "text" | "number" | "boolean" | "date" | "select";
  required: boolean;
  options: string[] | null;
  description: string | null;
}
export interface ProspectSchemaView {
  fields: ProspectFieldView[];
}

// 3.12 — error overview (mirrors ErrorEventController records).

export type ErrorSeverity = "WARNING" | "ERROR" | "CRITICAL";

export interface ErrorEventView {
  id: string;
  strategyId: string | null;
  severity: ErrorSeverity;
  source: string;
  /** Stable identity of the kind of failure; null on rows written before V29. */
  code: string | null;
  summary: string;
  detail: string | null;
  refType: string | null;
  refId: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

export interface ErrorSummary {
  unacknowledged: number;
  unacknowledgedBySeverity: Record<ErrorSeverity, number>;
}

// 2.8 — strategy flowchart. Mirrors StrategyGraphService's records. The graph is
// derived server-side from the strategy document; the SPA renders it and never
// parses YAML itself.

export interface GraphNode {
  slug: string;
  name: string;
  order: number;
  terminal: boolean;
  goal: string | null;
  /** Null on a draft (unsaved) graph — no persisted phase row to point at. */
  phaseId: string | null;
  activeManualPrompts: number;
  hasDocumentOutput: boolean;
  qualificationQuestionCount: number;
  aiRuleCount: number;
  computedFieldCount: number;
  ragTopics: string[];
}

export interface GraphEdge {
  from: string;
  /** Null when the rule declares no target — rendered as a broken transition. */
  to: string | null;
  label: string;
  description: string | null;
  kind: "legacy" | "compound";
  priority: number;
  setStatus: string | null;
  requiresHumanApproval: boolean;
  dangling: boolean;
}

export interface StrategyGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/** Validation outcome; `graph` is the preview of a valid document. */
export interface StrategyValidateResult {
  valid: boolean;
  error: string | null;
  graph: StrategyGraph | null;
}

export interface StrategyYamlView {
  yaml: string;
}

// 2.8 — visual editor. The document is the strategy YAML as a JSON tree with
// its original snake_case keys, so field edits write back under the same names.

export interface StrategyConfigView {
  document: StrategyDocument;
  configHash: string;
}

/** The YAML tree under no schema — the editor reads known keys defensively. */
export interface StrategyDocument {
  strategy?: {
    [key: string]: unknown;
    phases?: PhaseDocument[];
    safeguards?: string[];
    prospect_schema?: { fields?: ProspectFieldDocument[] };
  };
}

/**
 * One phase's block, under the YAML's own key names. Every member is optional
 * because the document is client-authored and the editor must render whatever
 * it finds; the typed members mirror the backend's StrategyConfigDoc.Phase.
 */
export interface PhaseDocument {
  [key: string]: unknown;
  id?: string;
  slug?: string;
  name?: string;
  order?: number;
  goal?: string;
  terminal?: boolean;
  max_emails_in_sequence?: number;
  delay_between_emails_hours?: number;
  cool_off_days?: number;
  rag_filter?: { topics?: string[] };
  manual_prompt?: string;
  email_template?: string;
  email_template_mode?: EmailTemplateMode;
  email_examples?: string[];
  safeguards?: string[];
  ai_rules?: string[];
  transition_rules?: LegacyTransitionRuleDocument[];
  transitions?: TransitionRuleDocument[];
  qualification_questions?: QualificationQuestionDocument[];
  computed_fields?: ComputedFieldDocument[];
  output?: PhaseOutputDocument;
}

/** `fixed`: sent as written, no model call. `draft`: the model adapts it. */
export type EmailTemplateMode = "fixed" | "draft";

/** Classifier-named rule: the reply classifier is told the condition name. */
export interface LegacyTransitionRuleDocument {
  [key: string]: unknown;
  condition?: string;
  description?: string;
  next_phase?: string;
  set_status?: string;
  requires_human_approval?: boolean;
}

export interface TransitionRuleDocument {
  [key: string]: unknown;
  name?: string;
  priority?: number;
  when?: PredicateDocument | null;
  then?: { [key: string]: unknown; next_phase?: string; set_status?: string;
           requires_human_approval?: boolean };
}

/**
 * The compound predicate grammar (CompoundPredicateEvaluator): a group of
 * `all` / `any` / `not`, or a leaf `{field, op, value}`.
 */
export type PredicateDocument =
  | { all: PredicateDocument[] }
  | { any: PredicateDocument[] }
  | { not: PredicateDocument }
  | PredicateLeafDocument;

export interface PredicateLeafDocument {
  field: string;
  op: PredicateOp;
  value?: unknown;
}

export type PredicateOp =
  | "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "in" | "not_in" | "exists" | "matched";

export interface QualificationQuestionDocument {
  [key: string]: unknown;
  key?: string;
  prompt?: string;
  type?: "text" | "number" | "boolean" | "enum";
  required?: boolean;
  options?: string[];
}

export interface ComputedFieldDocument {
  [key: string]: unknown;
  key?: string;
  expression?: string;
}

export interface PhaseOutputDocument {
  [key: string]: unknown;
  type?: string;
  template?: string;
  when?: PredicateDocument | null;
  attach_to_email?: boolean;
  deal_context?: Record<string, unknown>;
}

export interface ProspectFieldDocument {
  [key: string]: unknown;
  key?: string;
  label?: string;
  type?: string;
}

/** One field edit, mirrored by StrategyPatchService.PatchOp. */
export interface StrategyPatchOp {
  scope: "strategy" | "phase";
  phase: string | null;
  keyPath: string[];
  value: unknown;
}

export interface DocumentPatchResult {
  valid: boolean;
  error: string | null;
  graph: StrategyGraph | null;
  configHash: string;
}

export interface StrategyLayoutView {
  layout: { positions?: Record<string, { x: number; y: number }> } | null;
  configHash: string;
}

// 2.17/3.9 — held phase transitions awaiting human approval.

export interface PendingTransitionView {
  id: string;
  strategyId: string;
  prospectId: string;
  prospectEmail: string | null;
  prospectFullName: string | null;
  fromPhaseSlug: string | null;
  fromPhaseName: string | null;
  toPhaseSlug: string | null;
  toPhaseName: string | null;
  triggerCondition: string | null;
  setStatus: string | null;
  inboundEmailId: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string;
  decidedAt: string | null;
}

// Ingest injection screen — chunks held back for review.

export interface QuarantinedChunk {
  id: string;
  contentId: string;
  chunkIndex: number;
  text: string;
  reason: string | null;
  createdAt: string;
}

export interface DashboardOverview {
  totalProspects: number;
  activeStrategies: number;
  prospectsByStatus: Record<ProspectStatus, number>;
  emails24h: {
    sent: number;
    delivered: number;
    opened: number;
    replied: number;
    bounced: number;
    failed: number;
  };
  llmSpentTodayEur: number;
  llmDailyCapEur: number;
}


// ---------------------------------------------------------------------------
// Pipeline board (StrategyBoardController) — one column per strategy phase,
// in declared order, plus a synthetic bucket for prospects not yet in any
// phase. Columns are whatever the strategy document configures; the board
// never assumes a particular journey.
// ---------------------------------------------------------------------------

export interface BoardCard {
  id: string;
  email: string;
  fullName: string | null;
  status: ProspectStatus;
  flowState: FlowState;
  awaitingApproval: boolean;
  qualificationAnswered: number;
  qualificationTotal: number;
  nextActionAt: string | null;
  lastActivityAt: string | null;
  /**
   * The most recent note a human left on a phase change for this prospect —
   * why they are in the column they are in. Null when nobody has written one.
   */
  latestNote: string | null;
}

export interface BoardColumn {
  /** Null only on the synthetic "not started" bucket. */
  phaseId: string | null;
  phaseSlug: string | null;
  phaseName: string | null;
  /** -1 on the synthetic bucket; otherwise the phase's declared order. */
  orderIndex: number;
  terminal: boolean;
  /** Full population of the column; `cards` may be a capped preview of it. */
  totalCount: number;
  cards: BoardCard[];
}

export interface BoardView {
  strategyId: string;
  strategySlug: string;
  columns: BoardColumn[];
  unassigned: BoardColumn;
}

/**
 * Operator override of a prospect's phase (`POST /prospects/:id/phase`). The
 * note is optional but is the point of the route: it is the only channel by
 * which something that happened off-email — a call, a meeting — reaches the
 * agent's context for the next follow-up.
 */
export interface PhaseMoveRequest {
  toPhaseId: string;
  note: string | null;
}

export interface PhaseMoveResult {
  prospectId: string;
  fromPhaseId: string | null;
  toPhaseId: string;
  toPhaseName: string;
  /** A terminal target stops the agent working this prospect. */
  terminal: boolean;
  flowState: FlowState;
  /** Non-null only when the move re-armed a running conversation. */
  agentResumesAt: string | null;
  note: string | null;
}

// ---------------------------------------------------------------------------
// Flow control
// ---------------------------------------------------------------------------

/**
 * What became of the tick an operation asked for. `QUEUED` covers a fresh tick
 * and a waiting one brought forward alike; `ALREADY_RUNNING` means a worker is
 * on this prospect right now.
 */
export type TickDisposition = "QUEUED" | "ALREADY_RUNNING";

/** A gate that can hold a queued tick back until it opens. */
export type TickGate = "SEND_WINDOW" | "RATE_LIMIT";

export interface FlowView {
  prospectId: string;
  flowState: FlowState | null;
  nextActionAt: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  /** Non-null when the operation was declined, with the reason to surface. */
  refusedReason: string | null;
  /** Set by operations that ask for a tick (start, run now); null otherwise. */
  tickDisposition: TickDisposition | null;
  /** The gate that will hold that tick back, when one is closed right now. */
  deferredBy: TickGate | null;
  /** When that gate opens. */
  deferredUntil: string | null;
}

export interface BulkFlowResult {
  accepted: number;
  refused: number;
  results: FlowView[];
}

// ---------------------------------------------------------------------------
// Email oversight + approval
// ---------------------------------------------------------------------------

export interface EmailSummary {
  id: string;
  threadId: string;
  prospectId: string;
  prospectEmail: string | null;
  prospectName: string | null;
  strategyId: string;
  phaseSlug: string | null;
  direction: EmailDirection;
  status: EmailStatus;
  subject: string;
  bodyPreview: string;
  edited: boolean;
  awaitingApproval: boolean;
  /** The ingest-time zero-trust screen flagged this inbound as a possible prompt injection. */
  injectionSuspected: boolean;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
}

export interface EmailDetail {
  id: string;
  threadId: string;
  prospectId: string;
  prospectEmail: string | null;
  prospectName: string | null;
  strategyId: string;
  phaseId: string | null;
  phaseSlug: string | null;
  direction: EmailDirection;
  status: EmailStatus;
  subject: string;
  bodyText: string;
  bodyHtml: string | null;
  /** The model's original, retained once a reviewer edits. Null if untouched. */
  generatedSubject: string | null;
  generatedBodyText: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  editedBy: string | null;
  editedAt: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  bounceReason: string | null;
  createdAt: string;
  /** The ingest-time zero-trust screen flagged this inbound as a possible prompt injection. */
  injectionSuspected: boolean;
}

// ---------------------------------------------------------------------------
// Per-strategy sandbox rehearsal settings
// ---------------------------------------------------------------------------

export type SandboxMode = "isolated" | "test_address";

export interface SandboxView {
  mode: SandboxMode;
  testAddress: string | null;
  provider: string | null;
  autoReply: boolean;
  /** False when the deployment is live, in which case these settings are inert. */
  deploymentSandboxed: boolean;
  /** Real providers this deployment can route through; excludes sandbox. */
  availableProviders: string[];
}

export interface SandboxUpdate {
  mode: SandboxMode;
  testAddress: string | null;
  provider: string | null;
  autoReply: boolean;
}

// ---------------------------------------------------------------------------
// Deployment email mode — drives the sandbox banner
// ---------------------------------------------------------------------------

export interface EmailModeView {
  providerId: string;
  effectiveAttachmentProviderId: string;
  /** True when nothing this deployment "sends" actually leaves the machine. */
  sandbox: boolean;
  /** Whether the sandbox rehearsal routes exist on this deployment. */
  simulationAvailable: boolean;
  /** False when the provider emits no webhooks, so messages stop at SENT. */
  deliveryEvents: boolean;
  inboundPollingEnabled: boolean;
  notice: string | null;
}

export interface SimulateInboundResult {
  prospectId: string;
  ingested: boolean;
  messageId: string;
  note: string;
}

export type SimulatedEvent =
  | "DELIVERED"
  | "OPENED"
  | "REPLIED"
  | "BOUNCED"
  | "COMPLAINED"
  | "UNSUBSCRIBED";

// ---------------------------------------------------------------------------
// Contact-list import (LeadImportController)
// ---------------------------------------------------------------------------

/** Where one source column's values go. Mirrors ColumnMapping.Target. */
export type ImportTarget =
  | "EMAIL"
  | "PHONE"
  | "FULL_NAME"
  | "SOURCE"
  | "TIMEZONE"
  | "CUSTOM_FIELD"
  | "IGNORE";

export interface ImportMappingEntry {
  /** Source column header, matched case-/whitespace-insensitively server-side. */
  header: string;
  target: ImportTarget;
  /** Position among repeated columns for the same target (0-based). */
  ordinal: number;
  /** custom_fields key ([a-z0-9_]{1,40}); required for CUSTOM_FIELD, else null. */
  fieldKey: string | null;
}

export interface ImportMapping {
  entries: ImportMappingEntry[];
}

export type ImportBatchStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "COMPLETED_WITH_ERRORS"
  | "FAILED";

export interface ImportBatchView {
  id: string;
  strategyId: string;
  sourceLabel: string;
  status: ImportBatchStatus;
  rowsTotal: number;
  rowsImported: number;
  rowsSkipped: number;
  rowsFailed: number;
  prospectsCreated: number;
  activatedAt: string | null;
  errorSummary: string | null;
  createdAt: string;
}

/** One source row the importer did not turn into a prospect, and why. */
export interface ImportRowOutcome {
  rowNumber: number;
  reason: string;
}

export interface ImportResponse {
  batch: ImportBatchView;
  skipped: ImportRowOutcome[];
  failed: ImportRowOutcome[];
  warnings: string[];
  /** prospect_schema keys this import declared on the strategy. */
  schemaFieldsAdded: string[];
  /**
   * Rows carrying an address the strategy already refused to mail, with what
   * was done about each — dropped, or re-imported with the opt-out lifted.
   * Reasons name the address by its position on the row, never its value.
   */
  optedOut: ImportRowOutcome[];
  /** The policy that was applied to those rows. */
  optOutReimport: OptOutReimportMode;
}

/** Why the preview proposed the target it did. Mirrors ColumnSuggester.Origin. */
export type ImportSuggestionOrigin =
  | "REMEMBERED"
  | "SCHEMA"
  | "VALUE_SHAPE"
  | "FALLBACK";

/** One column of an uploaded sheet, as the server reads it. */
export interface ImportPreviewColumn {
  index: number;
  header: string;
  /** A few non-blank values, so the operator can recognise the column. */
  samples: string[];
  target: ImportTarget;
  ordinal: number;
  fieldKey: string | null;
  /** prospect_schema type inferred from the values; null unless CUSTOM_FIELD. */
  fieldType: string | null;
  label: string;
  origin: ImportSuggestionOrigin;
  /** Already declared on the strategy, so it must not be offered for adding again. */
  declaredInSchema: boolean;
}

export interface ImportPreview {
  columns: ImportPreviewColumn[];
  sampledRows: number;
  rememberedFor: number;
  notes: string[];
}

/** A column to declare on the strategy's prospect_schema as part of an import. */
export interface ImportSchemaField {
  key: string;
  label: string;
  type: string;
}

export interface ImportActivationView {
  batchId: string;
  activated: number;
  firstDueAt: string | null;
  lastDueAt: string | null;
}

// ---------------------------------------------------------------------------
// Qualification read model (ProspectQualificationController)
// ---------------------------------------------------------------------------

export interface QualificationQuestionView {
  key: string;
  prompt: string;
  type: string;
  required: boolean;
  options: string[];
  answered: boolean;
  /** Extracted answer as raw JSON — a scalar for scalar question types. */
  answer: unknown;
  confidence: number | null;
  extractedAt: string | null;
}

/** An answer extracted under a phase the prospect has since left. */
export interface CarriedAnswerView {
  key: string;
  answer: unknown;
  extractedAt: string | null;
}

export interface QualificationView {
  phaseSlug: string | null;
  phaseName: string | null;
  questions: QualificationQuestionView[];
  answeredCount: number;
  totalCount: number;
  carriedAnswers: CarriedAnswerView[];
}

// ---------------------------------------------------------------------------
// Reference documents
// ---------------------------------------------------------------------------

export type ReferenceDocumentStatus = "READY" | "QUARANTINED" | "FAILED";

/**
 * ALL_PHASES applies to every phase of the strategy, including ones added
 * later; SELECTED_PHASES applies only to the listed slugs.
 */
export type ReferenceDocumentScope = "ALL_PHASES" | "SELECTED_PHASES";

/**
 * An operator-uploaded document the agent can read as standing context, and/or
 * fill in from a conversation and send.
 *
 * <p>The uploaded file itself is not kept — only the text extracted from it —
 * so there is no download URL here by design.
 */
export interface ReferenceDocumentView {
  id: string;
  strategyId: string;
  slug: string;
  title: string;
  description: string | null;
  sourceFilename: string;
  sourceKind: string;
  extractedChars: number;
  status: ReferenceDocumentStatus;
  statusReason: string | null;
  enabled: boolean;
  scope: ReferenceDocumentScope;
  phaseSlugs: string[];
  useAsPromptContext: boolean;
  fillable: boolean;
  requiresApproval: boolean;
  contextTokenBudget: number;
  markerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReferenceDocumentDetail {
  document: ReferenceDocumentView;
  extractedText: string;
}

export interface ReferenceDocumentSupportedTypes {
  extensions: string[];
}

/** Every field optional: the page PATCHes only what the operator changed. */
export interface ReferenceDocumentUpdate {
  title?: string;
  description?: string;
  enabled?: boolean;
  scope?: ReferenceDocumentScope;
  phaseSlugs?: string[];
  useAsPromptContext?: boolean;
  fillable?: boolean;
  requiresApproval?: boolean;
  contextTokenBudget?: number;
}
